import MaterialUiCheckBox from '@mui/material/Checkbox';
import type { FormControlLabelProps } from '@mui/material/FormControlLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTrackers } from '@ps/web-shared-analytics';
import type { LabelProp } from '@ps/web-shared-components';
import { useDisplayLabel, useTrackingLabel } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import type { FieldMetaState } from 'react-final-form';
import { useSelector } from 'react-redux';

import CheckboxCheckedIcon from '@swf/core/assets/icons/checkbox-selected.svg';
import CheckboxOutlineIcon from '@swf/core/assets/icons/checkbox.svg';
import { select } from '@swf/core/store';

import {
  disabled as checkBoxDisabled,
  label as checkBoxLabel,
  checkbox,
  checkboxError,
  labelError,
  root
} from './Checkbox.scss';

type Props = {
  className?: string;
  meta?: FieldMetaState<string>;
  compoundLabel?: React.ReactNode;
  disabled?: boolean;
  label: LabelProp;
} & Pick<FormControlLabelProps, 'name' | 'checked' | 'onChange' | 'value'>;

const Checkbox: React.FunctionComponent<Props> = ({
  meta,
  className,
  compoundLabel,
  label,
  disabled,
  onChange,
  ...rest
}) => {
  const { inputFilled } = useTrackers();
  const error = meta?.touched && meta?.invalid;
  const displayLabel = useDisplayLabel(label);
  const fieldName = useTrackingLabel(label);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const iconRef = React.useRef<HTMLInputElement | null>(null);

  const stopLabelToggle = (e: React.SyntheticEvent<HTMLLabelElement>) => {
    const isLink = (e.target as HTMLElement).closest('a');
    if (!isMobileEmbedded) return;
    if (iconRef.current?.contains(e.target as Node)) return;
    e.preventDefault();
    if (!isLink) {
      e.stopPropagation();
    }
  };

  return (
    <FormControlLabel
      {...rest}
      onPointerDownCapture={stopLabelToggle as React.PointerEventHandler<HTMLLabelElement>}
      onClickCapture={stopLabelToggle as React.MouseEventHandler<HTMLLabelElement>}
      label={displayLabel}
      onChange={(event, checked) => {
        onChange?.(event, checked);
        inputFilled({ fieldName, fieldType: 'checkbox', boolInput: checked });
      }}
      control={
        <MaterialUiCheckBox
          icon={<CheckboxOutlineIcon />}
          slotProps={{ input: { ref: iconRef } }}
          checkedIcon={
            <CheckboxCheckedIcon
              className={classNames({
                [checkBoxDisabled]: disabled
              })}
            />
          }
          classes={{
            root: classNames(checkbox, { [checkboxError]: error })
          }}
          disabled={disabled}
        />
      }
      classes={{
        label: classNames(className, checkBoxLabel, {
          [labelError]: error
        }),
        root
      }}
    />
  );
};

export default Checkbox;
